import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Route, Routes, Navigate, useParams } from 'react-router';
import LoadSpinner from './components/LoadSpinner';
import config from '../src/config';
import UserInfoPage from './components/user/UserInfoPage';
import { getUser } from './store/selectors/user';
import { connect } from 'react-redux';
import { OIDCCallBack } from './components/OIDCCallBack';

const HomeContainer = lazy(() => import(
  /* webpackChunkName: "home" */ './views/Home'));
const Info = lazy(() => import(
  /* webpackChunkName: "info" */ './views/md/Info'));
const Participate = lazy(() => import(
  /* webpackChunkName: "participate" */ './views/md/Participate'));
const HostInstructions = lazy(() => import(
  /* webpackChunkName: "host_instructions" */ './views/md/HostInstructions'));
const TermsOfUseAndModeration = lazy(() => import(
  /* webpackChunkName: "host_instructions" */ './views/md/TermsOfUseAndModeration'));
const HearingCreationInstructions = lazy(() => import(
    /* webpackChunkName: "hearing_creation_instructions" */ './views/md/HearingCreationInstructions'));
const AccessibilityInfo = lazy(() => import(
  /* webpackChunkName: "accessibility_info" */ './views/md/AccessibilityInfo'));
const PrivacyInfo = lazy(() => import(
  /* webpackChunkName: "privacy_info" */ './views/md/PrivacyInfo'));
const HearingsContainer = lazy(() => import(
  /* webpackChunkName: "hearings" */'./views/Hearings/Hearings'));
const HearingContainer = lazy(() => import(
  /* webpackChunkName: "hearing" */'./views/Hearing/HearingContainer'));
const NewHearingContainer = lazy(() => import(
  /* webpackChunkName: "newhearing" */'./views/NewHearing/NewHearingContainer'));
//const FullscreenHearingContainer = lazy(() => import(
//  /* webpackChunkName: "fullscreen" */'./views/FullscreenHearing/FullscreenHearingContainer'));
const Login = lazy(() => import(
  /* webpackChunkName: "auth" */'./views/Auth/Login'));
const Registration = lazy(() => import(
  /* webpackChunkName: "auth" */'./views/Auth/Registration'));
const PasswordReset = lazy(() => import(
  /* webpackChunkName: "auth" */'./views/Auth/PasswordReset'));
const PasswordResetConfirm = lazy(() => import(
  /* webpackChunkName: "auth" */'./views/Auth/PasswordResetConfirm'));
const RegistrationSuccess = lazy(() => import(
  /* webpackChunkName: "auth" */'./views/Auth/RegistrationSuccess'));
const Confirm = lazy(() => import(
  /* webpackChunkName: "auth" */'./views/Auth/Confirmation'));
const ConfirmSuccess = lazy(() => import(
  /* webpackChunkName: "auth" */'./views/Auth/ConfirmationSuccess'));
const JoinHearing = lazy(() => import(
  /* webpackChunkName: "hearings" */'./views/Hearings/JoinHearing'));
const JoinHearingFacilitator = lazy(() => import(
  /* webpackChunkName: "hearings" */'./views/Hearings/JoinHearingFacilitator'));
const FinalStatement = lazy(() => import(
  /* webpackChunkName: "finalstatement" */'./views/Hearings/FinalStatement'));
const FinalStatementPreview = lazy(() => import(
  /* webpackChunkName: "finalstatementPreview" */'./views/Hearings/FinalStatementPreview'));

/* Vanilla Redirect component can't handle dynamic rerouting,
 * so we need Redirector to access params for the hearingSlug
 */
const Redirector = () => {
  const { hearingSlug } = useParams();
  return (
    <div>
      <Navigate to={`/${hearingSlug}`} />
    </div>
  );
};


const AppRoutes = ({
                  user,
                }) => {
  const token = sessionStorage.getItem('token');
  return (
    <Suspense fallback={<LoadSpinner />}>
      <Routes>
        <Route exact path="/" Component={props => <HomeContainer {...props} />} />
        <Route path="/user-info" element={!user && !token ? <Navigate to="/" /> : <UserInfoPage />} />
        <Route path="/auth/login" Component={props => <Login {...props} />} />
        <Route path="/auth/registration/success"
               Component={props => <RegistrationSuccess {...props} />} />
        <Route path="/auth/registration" Component={props => <Registration {...props} />} />
        <Route path="/password-reset/confirm/:uid/:token"
               Component={props => <PasswordResetConfirm {...props} />} />
        <Route path="/password-reset" Component={props => <PasswordReset {...props} />} />
        <Route path="/auth/confirm/success" Component={props => <ConfirmSuccess {...props} />} />
        <Route path="/auth/confirm/:confirmationKey" Component={props => <Confirm {...props} />} />
        <Route path="/info" Component={props => <Info {...props} />} />
        {config.showAccessibilityInfo && (
          <Route path="/accessibility" Component={props => <AccessibilityInfo {...props} />} />
        )}
        <Route path="/oidc/callback/" Component={props => <OIDCCallBack {...props} />} />
        <Route path="/privacy" Component={props => <PrivacyInfo {...props} />} />
        <Route path="/participate" Component={props => <Participate {...props} />} />
        <Route path="/host-instructions" Component={props => <HostInstructions {...props} />} />
        <Route path="/hearing-creation-instructions" Component={props => <HearingCreationInstructions {...props} />} />
        <Route path="/terms-of-use-and-moderation" Component={props => <TermsOfUseAndModeration {...props} />} />
        <Route path="/hearings/:tab" Component={props => <HearingsContainer {...props} />} />
        <Route path="/hearing/new" Component={props => <NewHearingContainer {...props} />} />
        <Route path="/hearing/:hearingSlug" Component={props => <Redirector {...props} />} />
        <Route path="/:hearingSlug/join/:joinToken" Component={props => <JoinHearing {...props} />} />
        <Route path="/:hearingSlug/join_facilitator/:joinToken"
               Component={props => <JoinHearingFacilitator {...props} />} />
        <Route path="/statement/preview/:statementId"
               Component={props => <FinalStatementPreview {...props} />} />
        <Route path="/:hearingId/statement/" Component={props => <FinalStatement {...props} />} />
        <Route exact path="/:hearingSlug/*" Component={props => <HearingContainer {...props} />} />
      </Routes>
    </Suspense>
  );
};

const mapStateToProps = (state) => ({
  user: getUser(state),
});


AppRoutes.propTypes = {
  user: PropTypes.object,
};
export default connect(mapStateToProps)(AppRoutes);
